/**
 * CityMatcher — budget-first city discovery (2026-02-29).
 *
 * A student sets a monthly budget in their preferred currency, tunes
 * safety + student-friendliness floors, optionally filters by degree,
 * and gets a ranked list of cities that fit their money + preferences.
 *
 * Backed by /api/cities/matcher — see routes/cities.py::match_cities.
 */
import { SubjectsBadge } from "@/components/SubjectsBadge";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCurrency } from "@/context/CurrencyContext";
import { useJourney } from "@/context/JourneyContext";
import {
  Coins, ShieldCheck, GraduationCap, MapPin, Sparkles, Users,
  ArrowRight, Search, Sliders, Wallet, Scale,
  Briefcase, Train, Palette, Building2, HelpCircle, CloudSun, X,
} from "lucide-react";
import ConfirmPillarPanel from "@/components/ConfirmPillarPanel";
import PlaceReadinessCard from "@/components/PlaceReadinessCard";
import SEOHead from "@/components/app/SEOHead";
import { useCityShortlist } from "@/hooks/useCityShortlist";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

const SAFETY_LABELS = {
  1: "Any (no filter)",
  2: "Some caution OK",
  3: "Moderate or safer",
  4: "Generally safe",
  5: "Very safe only",
};

const SAFETY_COLOR = {
  "A+": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "A":  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "B":  "bg-amber-50 text-amber-800 border-amber-200",
  "C":  "bg-orange-50 text-orange-800 border-orange-200",
  "D":  "bg-red-50 text-red-800 border-red-200",
};

const COUNTRY_QUICK_PICKS = [
  { code: "",   label: "All countries" },
  { code: "UK", label: "🇬🇧 UK" },
  { code: "US", label: "🇺🇸 US" },
  { code: "DE", label: "🇩🇪 Germany" },
  { code: "NL", label: "🇳🇱 Netherlands" },
  { code: "IE", label: "🇮🇪 Ireland" },
  { code: "CA", label: "🇨🇦 Canada" },
  { code: "AU", label: "🇦🇺 Australia" },
  { code: "JP", label: "🇯🇵 Japan" },
  { code: "KR", label: "🇰🇷 South Korea" },
  { code: "SG", label: "🇸🇬 Singapore" },
  { code: "AE", label: "🇦🇪 UAE" },
];

const CAREER_SECTORS = [
  { key: "tech",        label: "Tech" },
  { key: "finance",     label: "Finance" },
  { key: "law",         label: "Law" },
  { key: "creative",    label: "Creative & Media" },
  { key: "healthcare",  label: "Healthcare" },
  { key: "engineering", label: "Engineering" },
  { key: "startups",    label: "Startups" },
  { key: "government",  label: "Government / Policy" },
  { key: "research",    label: "Research" },
];

const INDUSTRIES_FINE = [
  { key: "fintech",          label: "Fintech" },
  { key: "biotech",          label: "Biotech" },
  { key: "aerospace",        label: "Aerospace" },
  { key: "cleantech",        label: "Cleantech" },
  { key: "gaming",           label: "Gaming & Media" },
  { key: "publishing",       label: "Publishing" },
  { key: "content_creation", label: "Digital Content" },
  { key: "agencies",         label: "Creative Agencies" },
  { key: "ecommerce",        label: "Ecommerce" },
  { key: "mobility",         label: "Mobility & Logistics" },
  { key: "agritech",         label: "AgriTech" },
  { key: "edtech",           label: "EdTech" },
];

const TRANSPORT_MODES = [
  { key: "rail",  label: "Rail",  icon: Train },
  { key: "air",   label: "Air",   icon: ArrowRight },
  { key: "walk",  label: "Walk",  icon: MapPin },
  { key: "cycle", label: "Cycle", icon: ArrowRight },
  { key: "night", label: "Night", icon: Sparkles },
];

const LIFESTYLE_TAGS = [
  { key: "arts",               label: "Arts & Culture" },
  { key: "museums",            label: "Museums" },
  { key: "food",               label: "Food Scene" },
  { key: "green",              label: "Green Spaces" },
  { key: "nightlife",          label: "Nightlife" },
  { key: "sports_participate", label: "Sports (participate)" },
  { key: "sports_watch",       label: "Sports (watch)" },
  { key: "outdoors",           label: "Outdoors" },
  { key: "beach",              label: "Beaches" },
  { key: "diverse",            label: "Diverse Community" },
  { key: "live_music",         label: "Live Music" },
  { key: "cafe",               label: "Cafe Culture" },
  { key: "festivals",          label: "Festivals" },
];

const SETTING_LABELS = {
  1: "Campus town",
  2: "Small city",
  3: "Mid-size city",
  4: "Big city",
  5: "Metropolis",
};

// Climate filter — mirrors WUA_Climate_Filter_Developer_Pack v1.0.
const CLIMATE_PREF_OPTIONS = [
  { key: "warm_sunny",   label: "Warm & sunny" },
  { key: "mild",         label: "Mild, no extremes" },
  { key: "four_seasons", label: "Four distinct seasons" },
  { key: "cool",         label: "Cooler weather" },
];
const CLIMATE_AVOID_OPTIONS = [
  { key: "extreme_heat",  label: "Extreme heat" },
  { key: "cold_winters",  label: "Cold winters" },
  { key: "humid_heat",    label: "Sticky, humid weather" },
  { key: "frequent_rain", label: "Frequent rain" },
  { key: "dark_winters",  label: "Short, dark winter days" },
];

const SECTOR_LABELS = Object.fromEntries(
  [
    { key: "tech",        label: "Tech" },
    { key: "finance",     label: "Finance" },
    { key: "law",         label: "Law" },
    { key: "creative",    label: "Creative & Media" },
    { key: "healthcare",  label: "Healthcare" },
    { key: "engineering", label: "Engineering" },
    { key: "startups",    label: "Startups" },
    { key: "government",  label: "Government / Policy" },
    { key: "research",    label: "Research" },
    { key: "fintech",   label: "Fintech" },
    { key: "biotech",   label: "Biotech" },
    { key: "aerospace", label: "Aerospace" },
    { key: "cleantech", label: "Cleantech" },
    { key: "gaming",    label: "Gaming & Media" },
    { key: "publishing",       label: "Publishing" },
    { key: "content_creation", label: "Digital Content" },
    { key: "agencies",         label: "Creative Agencies" },
    { key: "ecommerce", label: "Ecommerce" },
    { key: "mobility",  label: "Mobility & Logistics" },
    { key: "agritech",  label: "AgriTech" },
    { key: "edtech",    label: "EdTech" },
  ].map((s) => [s.key, s.label]),
);
const TAG_LABELS = Object.fromEntries(
  [
    { key: "arts",               label: "Arts & Culture" },
    { key: "museums",            label: "Museums" },
    { key: "food",               label: "Food Scene" },
    { key: "green",              label: "Green Spaces" },
    { key: "nightlife",          label: "Nightlife" },
    { key: "sports_participate", label: "Sports (participate)" },
    { key: "sports_watch",       label: "Sports (watch)" },
    { key: "outdoors",           label: "Outdoors" },
    { key: "beach",              label: "Beaches" },
    { key: "diverse",            label: "Diverse Community" },
    { key: "live_music",         label: "Live Music" },
    { key: "cafe",               label: "Cafe Culture" },
    { key: "festivals",          label: "Festivals" },
  ].map((t) => [t.key, t.label]),
);

/**
 * Build the "Why this fits" reasons for a card. Reasons are ordered by
 * user-signal strength: explicit filters the user turned on first,
 * then organic strengths (safety, budget slack). We only ever surface
 * the top 2 in the chip.
 */
function whyReasons(c, filters) {
  const reasons = [];
  const dims = c.dimensions || {};

  // 1) Active filter hits — user asked for these, so they rank first.
  if (filters.careerMin > 0 && (dims.career?.score || 0) >= filters.careerMin) {
    reasons.push({ icon: "briefcase", label: `Career ${dims.career.score}/5` });
  }
  if (filters.careerSectors.length && dims.career?.sectors?.length) {
    const hit = filters.careerSectors.filter((s) => dims.career.sectors.includes(s));
    if (hit.length) {
      reasons.push({
        icon: "briefcase",
        label: `${hit.map((s) => SECTOR_LABELS[s] || s).slice(0, 2).join(" · ")} sector`,
      });
    }
  }
  if (filters.industriesFine.length && dims.career?.industries_fine?.length) {
    const hit = filters.industriesFine.filter((s) => dims.career.industries_fine.includes(s));
    if (hit.length) {
      reasons.push({
        icon: "briefcase",
        label: `${hit.map((s) => SECTOR_LABELS[s] || s).slice(0, 2).join(" · ")} hub`,
      });
    }
  }
  if (filters.transportModes.length && dims.transport) {
    const modeScores = filters.transportModes
      .map((m) => ({ m, v: dims.transport[m] || 0 }))
      .sort((a, b) => b.v - a.v);
    const top = modeScores[0];
    if (top && top.v >= (filters.transportMin || 1)) {
      reasons.push({
        icon: "train",
        label: `${top.m[0].toUpperCase()}${top.m.slice(1)} ${top.v}/5`,
      });
    }
  }
  if ((filters.lifestyleMust.length || filters.lifestyleNice.length) && dims.lifestyle?.tags?.length) {
    const selected = [...filters.lifestyleMust, ...filters.lifestyleNice];
    const hit = selected.filter((t) => dims.lifestyle.tags.includes(t));
    if (hit.length) {
      const scores = dims.lifestyle.scores || {};
      const withScore = hit.slice(0, 2).map((t) => {
        const s = scores[t];
        return s != null
          ? `${TAG_LABELS[t] || t} ${s}/10`
          : (TAG_LABELS[t] || t);
      });
      reasons.push({
        icon: "palette",
        label: withScore.join(" · "),
      });
    }
  }
  if (filters.settingTarget > 0 && dims.setting) {
    const gap = Math.abs(dims.setting - filters.settingTarget);
    if (gap === 0) reasons.push({ icon: "building", label: `Exact ${SETTING_LABELS[dims.setting]}` });
    else if (gap === 1) reasons.push({ icon: "building", label: `Close to ${SETTING_LABELS[filters.settingTarget]}` });
  }
  if (c.climate?.status === "applied" && c.climate.score != null) {
    reasons.push({ icon: "cloud-sun", label: `Climate ${c.climate.score}/100` });
  }

  // 2) Organic strengths — used to fill up to 2 reasons.
  const grade = c.safety?.grade;
  if (grade === "A+" || grade === "A") {
    reasons.push({ icon: "shield", label: `Safety grade ${grade}` });
  }
  if ((c.budget_slack_usd || 0) >= 300) {
    reasons.push({ icon: "coins", label: `$${Math.round(c.budget_slack_usd)}/mo under budget` });
  }
  if ((c.student_friendliness_score || 0) >= 4) {
    reasons.push({ icon: "sparkles", label: `Student score ${c.student_friendliness_score}/5` });
  }

  // De-dup and cap at 2.
  const seen = new Set();
  return reasons.filter((r) => (seen.has(r.label) ? false : (seen.add(r.label), true))).slice(0, 2);
}

const REASON_ICON = {
  briefcase: Briefcase,
  train:     Train,
  palette:   Palette,
  building:  Building2,
  shield:    ShieldCheck,
  coins:     Coins,
  sparkles:  Sparkles,
};

export default function CityMatcher() {
  const navigate = useNavigate();
  const { currency, convert, format } = useCurrency();

  // Restore last-used filter blob from localStorage so a student who
  // opens a city detail and hits Back sees the matcher exactly the
  // way they left it. Written on every filter change below.
  const HYDRATED = (() => {
    try {
      const raw = localStorage.getItem("wua:cityMatcher:filters");
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch { return {}; }
  })();

  // Filter state — budget in display currency; convert to USD for the API.
  const [budgetDisplay, setBudgetDisplay] = useState(HYDRATED.budgetDisplay ?? 1500);
  const [minSafety, setMinSafety] = useState(HYDRATED.minSafety ?? 3);
  const [minFriendliness, setMinFriendliness] = useState(HYDRATED.minFriendliness ?? 3);
  const [country, setCountry] = useState(HYDRATED.country ?? "");
  const [degree, setDegree] = useState(HYDRATED.degree ?? "");
  const [debouncedDegree, setDebouncedDegree] = useState(HYDRATED.degree ?? "");
  const _cityShortlist = useCityShortlist();
  const compareSlugs = _cityShortlist.slugs;
  const setCompareSlugs = (updater) => {
    const cur = _cityShortlist.slugs;
    const next = typeof updater === "function" ? updater(cur) : updater;
    // Diff and apply via the hook's add/remove so its cap logic stays
    // the single source of truth.
    const before = new Set(cur);
    const after = new Set(next);
    cur.forEach((s) => { if (!after.has(s)) _cityShortlist.remove(s); });
    next.forEach((s) => { if (!before.has(s)) _cityShortlist.add(s); });
  };

  // Lifestyle & Fit filters (2026-02-29)
  const [careerMin, setCareerMin] = useState(HYDRATED.careerMin ?? 0);
  const [careerSectors, setCareerSectors] = useState(HYDRATED.careerSectors ?? []);
  const [industriesFine, setIndustriesFine] = useState(HYDRATED.industriesFine ?? []);
  const [showMoreIndustries, setShowMoreIndustries] = useState(false);
  const [transportModes, setTransportModes] = useState(HYDRATED.transportModes ?? []);
  const [transportMin, setTransportMin] = useState(HYDRATED.transportMin ?? 3);
  const [lifestyleMust, setLifestyleMust] = useState(HYDRATED.lifestyleMust ?? []);
  const [lifestyleNice, setLifestyleNice] = useState(HYDRATED.lifestyleNice ?? []);
  // Climate — max 2 preferences (OR), any number of avoidances.
  const [climatePrefs, setClimatePrefs] = useState(HYDRATED.climatePrefs ?? []);
  const [climateAvoids, setClimateAvoids] = useState(HYDRATED.climateAvoids ?? []);
  const [climateMin, setClimateMin] = useState(HYDRATED.climateMin ?? 50);
  const [settingTarget, setSettingTarget] = useState(HYDRATED.settingTarget ?? 0);   // 0 = any

  // Cross-pillar handoff: pre-seed sectors from either (a) URL param
  // ?career_sectors=healthcare,tech (shareable) or (b) the Course Fit
  // profile's sector_hint. The banner below tells the student where
  // the pre-selection came from so it doesn't feel magic.
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile: journeyProfile, updateProfile: updateJourneyProfile } = useJourney();
  const [handoffSource, setHandoffSource] = useState(null);   // "url" | "coursefit" | null

  useEffect(() => {
    // Only auto-seed once, on mount, and only if user hasn't already
    // picked sectors themselves.
    if (careerSectors.length > 0) return;
    const paramSectors = (searchParams.get("career_sectors") || "")
      .split(",").map((s) => s.trim()).filter(Boolean);
    if (paramSectors.length) {
      setCareerSectors(paramSectors);
      setHandoffSource("url");
      return;
    }
    const hint = journeyProfile?.what?.sector_hint;
    if (hint) {
      setCareerSectors([hint]);
      setHandoffSource("coursefit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyProfile?.what?.sector_hint]);

  const clearHandoff = () => {
    setCareerSectors([]);
    setHandoffSource(null);
    if (searchParams.get("career_sectors")) {
      searchParams.delete("career_sectors");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Reset every user-controlled filter back to its default. Clears
  // both the localStorage blob and any sector-handoff copy so the
  // student sees a truly blank slate.
  const resetFilters = () => {
    setBudgetDisplay(1500);
    setMinSafety(3);
    setMinFriendliness(3);
    setCountry("");
    setDegree("");
    setCareerMin(0);
    setCareerSectors([]);
    setIndustriesFine([]);
    setTransportModes([]);
    setTransportMin(3);
    setLifestyleMust([]);
    setLifestyleNice([]);
    setClimatePrefs([]);
    setClimateAvoids([]);
    setClimateMin(50);
    setSettingTarget(0);
    setHandoffSource(null);
    try { localStorage.removeItem("wua:cityMatcher:filters"); } catch {}
    // Also strip any URL-carried sector handoff so a refresh doesn't
    // re-seed.
    if (searchParams.get("career_sectors")) {
      searchParams.delete("career_sectors");
      setSearchParams(searchParams, { replace: true });
    }
    toast.success("Filters cleared — back to defaults.");
  };

  // Are any user-controlled filters currently non-default? Used to
  // conditionally show the Clear-all chip so the header stays quiet
  // on a fresh visit.
  const hasCustomFilters = useMemo(() => (
    budgetDisplay !== 1500 ||
    minSafety !== 3 ||
    minFriendliness !== 3 ||
    country !== "" ||
    degree !== "" ||
    careerMin !== 0 ||
    careerSectors.length > 0 ||
    industriesFine.length > 0 ||
    transportModes.length > 0 ||
    transportMin !== 3 ||
    lifestyleMust.length > 0 ||
    lifestyleNice.length > 0 ||
    climatePrefs.length > 0 ||
    climateAvoids.length > 0 ||
    climateMin !== 50 ||
    settingTarget !== 0
  ), [
    budgetDisplay, minSafety, minFriendliness, country, degree,
    careerMin, careerSectors, industriesFine,
    transportModes, transportMin,
    lifestyleMust, lifestyleNice,
    climatePrefs, climateAvoids, climateMin,
    settingTarget,
  ]);

  // Persist every user-controlled filter back to localStorage so
  // hitting "Back" from a city detail restores the exact matcher
  // state the student left. Writes debounced-lightly via the effect
  // deps — every field is a primitive or array, so JSON.stringify
  // handles them cleanly. We also mirror the blob into the journey
  // profile (debounced) so the state survives a device switch, not
  // just a tab reload.
  const journeySyncTimer = useRef(null);
  useEffect(() => {
    const blob = {
      budgetDisplay, minSafety, minFriendliness, country, degree,
      careerMin, careerSectors, industriesFine,
      transportModes, transportMin,
      lifestyleMust, lifestyleNice,
      climatePrefs, climateAvoids, climateMin,
      settingTarget,
    };
    try { localStorage.setItem("wua:cityMatcher:filters", JSON.stringify(blob)); }
    catch {}
    // Mirror to backend via journey profile — debounced 1.2s so a
    // slider drag doesn't flood the API.
    if (journeySyncTimer.current) clearTimeout(journeySyncTimer.current);
    journeySyncTimer.current = setTimeout(() => {
      try { updateJourneyProfile({ where: { matcher_filters: blob } }); }
      catch {}
    }, 1200);
    return () => {
      if (journeySyncTimer.current) clearTimeout(journeySyncTimer.current);
    };
  }, [
    budgetDisplay, minSafety, minFriendliness, country, degree,
    careerMin, careerSectors, industriesFine,
    transportModes, transportMin,
    lifestyleMust, lifestyleNice,
    climatePrefs, climateAvoids, climateMin,
    settingTarget,
    updateJourneyProfile,
  ]);

  // On very first mount, if localStorage is empty but the journey
  // profile carries a saved matcher_filters blob from another device,
  // hydrate from there so a phone-to-laptop switch feels seamless.
  const journeyHydrationRef = useRef(false);
  useEffect(() => {
    if (journeyHydrationRef.current) return;
    if (!journeyProfile) return;
    const remote = journeyProfile?.where?.matcher_filters;
    if (!remote || typeof remote !== "object") return;
    const local = localStorage.getItem("wua:cityMatcher:filters");
    if (local) { journeyHydrationRef.current = true; return; }
    journeyHydrationRef.current = true;
    // Apply — only touch each setter when the remote value differs
    // from the current default to avoid clobbering an in-flight edit.
    if (typeof remote.budgetDisplay === "number") setBudgetDisplay(remote.budgetDisplay);
    if (typeof remote.minSafety === "number") setMinSafety(remote.minSafety);
    if (typeof remote.minFriendliness === "number") setMinFriendliness(remote.minFriendliness);
    if (typeof remote.country === "string") setCountry(remote.country);
    if (typeof remote.degree === "string") setDegree(remote.degree);
    if (typeof remote.careerMin === "number") setCareerMin(remote.careerMin);
    if (Array.isArray(remote.careerSectors)) setCareerSectors(remote.careerSectors);
    if (Array.isArray(remote.industriesFine)) setIndustriesFine(remote.industriesFine);
    if (Array.isArray(remote.transportModes)) setTransportModes(remote.transportModes);
    if (typeof remote.transportMin === "number") setTransportMin(remote.transportMin);
    if (Array.isArray(remote.lifestyleMust)) setLifestyleMust(remote.lifestyleMust);
    if (Array.isArray(remote.lifestyleNice)) setLifestyleNice(remote.lifestyleNice);
    if (Array.isArray(remote.climatePrefs)) setClimatePrefs(remote.climatePrefs);
    if (Array.isArray(remote.climateAvoids)) setClimateAvoids(remote.climateAvoids);
    if (typeof remote.climateMin === "number") setClimateMin(remote.climateMin);
    if (typeof remote.settingTarget === "number") setSettingTarget(remote.settingTarget);
  }, [journeyProfile]);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // ── Subject-aware badging ──────────────────────────────────────────
  // Reads every subject confirmed on the PICK stage (up to 3) so a
  // student who picked "Dance" + "Film" sees BOTH counts on each city
  // card and can toggle either independently. Rank-labels ("Leading
  // match", "Also worth exploring") are filtered out defensively.
  const RANK_LABEL_BLOCKLIST = new Set([
    "leading match", "close match", "also worth exploring",
  ]);
  const pickedSubjects = ((journeyProfile?.what?.subjects_confirmed) || [])
    .map((s) => (typeof s === "string" ? s : (s?.label || s?.slug)))
    .filter(Boolean)
    .filter((v) => !RANK_LABEL_BLOCKLIST.has(String(v).toLowerCase().trim()));
  // `perSubject` mirrors the backend shape: { subjectName: { citySlug: {count} } }
  const [perSubject, setPerSubject] = useState({});
  // Which subjects are currently in the "only show cities with…"
  // filter. Empty Set = no restriction. When populated the filter is
  // a UNION — a city qualifies if it offers ANY ticked subject.
  // Default: all picked subjects are ticked so the student lands on
  // an already-narrowed list and only widens if they want to.
  const [activeSubjectFilter, setActiveSubjectFilter] = useState(() => new Set());
  const subjectFilterSeeded = useRef(false);
  useEffect(() => {
    if (subjectFilterSeeded.current) return;
    if (!pickedSubjects.length) return;
    subjectFilterSeeded.current = true;
    setActiveSubjectFilter(new Set(pickedSubjects));
    // Also pre-populate the Degree substring input so the SERVER-side
    // matcher pre-narrows the pool to cities offering ANY of the
    // picked subjects (backend splits on commas and unions the regex).
    // Users can clear / edit — the client-side subject checkboxes
    // still handle the union among the toggled subset.
    if (!degree) setDegree(pickedSubjects.join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSubjects.join("|")]);
  useEffect(() => {
    if (!pickedSubjects.length) { setPerSubject({}); return; }
    let cancelled = false;
    (async () => {
      try {
        // axios auto-serialises array params as ?subject=A&subject=B
        // when we hand it an array, which matches the backend's Query
        // list handler.
        const { data } = await api.get("/cities/subject-coverage", {
          params: { subject: pickedSubjects },
          paramsSerializer: { indexes: null },
        });
        if (!cancelled) setPerSubject(data?.per_subject || {});
      } catch {
        if (!cancelled) setPerSubject({});
      }
    })();
    return () => { cancelled = true; };
    // Serialise the array so the effect only refires on real changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSubjects.join("|")]);

  // Helpers used by the results grid + banner.
  const offeredByCity = (slug) =>
    pickedSubjects.filter((subj) => (perSubject?.[subj]?.[slug]?.count || 0) > 0);
  const cityMatchesFilter = (slug) => {
    if (activeSubjectFilter.size === 0) return true;
    return pickedSubjects.some(
      (subj) => activeSubjectFilter.has(subj) && (perSubject?.[subj]?.[slug]?.count || 0) > 0
    );
  };

  // Convert display currency → USD for the API call.
  const budgetUsd = useMemo(() => {
    // The `convert(x)` returns x-usd → x-in-current-currency; we invert.
    // Simpler: use the rates directly, but convert(1) gives the rate.
    const rate = convert(1) || 1;
    return Math.round(budgetDisplay / rate);
  }, [budgetDisplay, convert]);

  // Debounce degree input (300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDegree(degree.trim()), 300);
    return () => clearTimeout(t);
  }, [degree]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const { data } = await api.get("/cities/matcher", {
          params: {
            budget_usd: budgetUsd,
            min_safety: minSafety,
            student_friendliness_min: minFriendliness,
            countries: country || "",
            degree_query: debouncedDegree,
            career_min: careerMin,
            career_sectors: careerSectors.join(","),
            industries_fine: industriesFine.join(","),
            transport_modes: transportModes.join(","),
            transport_min: transportModes.length ? transportMin : 0,
            lifestyle_must: lifestyleMust.join(","),
            lifestyle_nice: lifestyleNice.join(","),
            climate_prefs:  climatePrefs.join(","),
            climate_avoids: climateAvoids.join(","),
            climate_min:    climateMin,
            setting_target: settingTarget,
            setting_tolerance: 1,
            limit: 40,
          },
        });
        if (!cancelled) setResults(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [budgetUsd, minSafety, minFriendliness, country, debouncedDegree,
      careerMin, careerSectors, industriesFine, transportModes, transportMin,
      lifestyleMust, lifestyleNice, climatePrefs, climateAvoids, climateMin,
      settingTarget]);

  const toggleCompare = (slug) => {
    setCompareSlugs((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= 4) return prev;
      return [...prev, slug];
    });
  };

  const toggleInList = (setter) => (key) => {
    setter((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const toggleSector    = toggleInList(setCareerSectors);
  const toggleFine      = toggleInList(setIndustriesFine);
  const toggleTransport = toggleInList(setTransportModes);
  const toggleMust      = toggleInList(setLifestyleMust);
  const toggleNice      = toggleInList(setLifestyleNice);

  const openCompare = () => {
    if (compareSlugs.length < 2) return;
    navigate(`/cities/compare?slugs=${compareSlugs.join(",")}`);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title="City Matcher — find your student city on your budget | WhichUniversityAdvisor"
        description="Match to student cities that fit your monthly budget, safety comfort, and degree of choice. Currency-aware, 338 cities globally."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <Link to="/cities" data-testid="matcher-back-link"
              className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5">
          ← All city guides
        </Link>
        <SubjectsBadge className="mt-4" />
        <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight text-[#002147]">
          Find your city.<br />
          <span className="text-[#7C3AED]">On your budget.</span>
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl text-base">
          Slide your monthly budget, set how safe you want it, add a degree if you know it — we'll show every city that fits, ranked by safety and student life. All prices convert live to {currency}.
        </p>
        <div className="mt-5 inline-flex items-start gap-3 rounded-xl border border-amber-200 bg-[#FDF6EC] px-4 py-2.5 max-w-2xl">
          <Sparkles className="h-4 w-4 text-[#92400E] shrink-0 mt-0.5" />
          <div className="text-sm text-stone-700">
            <span className="font-semibold text-[#92400E]">Express chooser</span>
            <span className="text-stone-500"> · not sure where to start? </span>
            <Link
              to="/city-quiz"
              data-testid="matcher-quiz-shortcut"
              className="font-semibold text-[#002147] underline decoration-[#7C3AED] decoration-2 underline-offset-2 hover:decoration-[#002147]"
            >
              Take the 60-second quiz →
            </Link>
          </div>
        </div>
      </section>

      {/* Filters — sticky-ish card */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-6">
        <div className="bg-white border border-stone-200 rounded-xl p-6 lg:p-8 shadow-sm" data-testid="matcher-filters">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-[#7C3AED]" />
              <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Filters</span>
            </div>
            {hasCustomFilters && (
              <button
                type="button"
                onClick={resetFilters}
                data-testid="matcher-reset-filters"
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white text-stone-700 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors px-3 py-1 text-[11px] font-medium"
                title="Reset every filter to its default"
              >
                <RotateCcw className="h-3 w-3" />
                Clear all filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
            {/* Budget */}
            <div data-testid="matcher-budget-block">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                  <Wallet className="h-4 w-4 text-[#7C3AED]" /> Monthly budget
                </label>
                <span className="font-mono text-lg font-semibold text-[#002147]" data-testid="matcher-budget-value">
                  {format(budgetUsd, { decimals: 0 })}
                </span>
              </div>
              <Slider
                data-testid="matcher-budget-slider"
                value={[budgetDisplay]}
                min={300} max={5000} step={50}
                onValueChange={(v) => setBudgetDisplay(v[0])}
                className="mt-1"
              />
              <div className="flex justify-between text-[11px] text-stone-500 font-mono mt-1.5">
                <span>{format(300 / (convert(1) || 1), { decimals: 0 })}</span>
                <span>{format(5000 / (convert(1) || 1), { decimals: 0 })}+</span>
              </div>
              <p className="text-xs text-stone-500 mt-2">
                Total monthly living cost (rent + utilities + groceries + transport + a bit of fun).
              </p>
            </div>

            {/* Safety */}
            <div data-testid="matcher-safety-block">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-[#7C3AED]" /> Crime tolerance
                </label>
                <span className="text-xs font-medium text-[#002147]" data-testid="matcher-safety-value">
                  {SAFETY_LABELS[minSafety]}
                </span>
              </div>
              <Slider
                data-testid="matcher-safety-slider"
                value={[minSafety]}
                min={1} max={5} step={1}
                onValueChange={(v) => setMinSafety(v[0])}
                className="mt-1"
              />
              <div className="flex justify-between text-[11px] text-stone-500 mt-1.5">
                <span>Higher-crime OK</span>
                <span>Very safe only</span>
              </div>
              <p className="text-xs text-stone-500 mt-2">
                Uses Numbeo Crime Index (Jan 2026). Grade A+ = safest, D = most caution.
              </p>
            </div>

            {/* Student friendliness */}
            <div data-testid="matcher-friendliness-block">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-[#7C3AED]" /> Student-friendliness floor
                </label>
                <span className="text-xs font-medium text-[#002147]" data-testid="matcher-friendliness-value">
                  ≥ {minFriendliness}/5
                </span>
              </div>
              <Slider
                data-testid="matcher-friendliness-slider"
                value={[minFriendliness]}
                min={1} max={5} step={1}
                onValueChange={(v) => setMinFriendliness(v[0])}
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-2">
                How much the city is set up for students: transport discounts, 24h libraries, social scene.
              </p>
            </div>

            {/* Degree */}
            <div data-testid="matcher-degree-block">
              <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5 mb-2">
                <GraduationCap className="h-4 w-4 text-[#7C3AED]" /> Degree (optional)
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  data-testid="matcher-degree-input"
                  value={degree}
                  onChange={(e) => setDegree(e.target.value)}
                  placeholder="e.g. Computer Science, Nursing, Law"
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-stone-500 mt-2">
                Show only cities where at least one university offers this.
                Separate multiple subjects with commas — any match keeps the city.
              </p>
            </div>
          </div>

          {/* Country chips */}
          <div className="mt-6" data-testid="matcher-country-chips">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Country
            </div>
            <div className="flex flex-wrap gap-2">
              {COUNTRY_QUICK_PICKS.map((c) => (
                <button
                  key={c.code || "all"}
                  data-testid={`matcher-country-${c.code || "all"}`}
                  onClick={() => setCountry(c.code)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition ${
                    country === c.code
                      ? "bg-[#002147] text-white border-[#002147]"
                      : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lifestyle & Fit — 4 dimension groups */}
          <div className="mt-8 pt-6 border-t border-stone-200" data-testid="matcher-lifestyle-fit">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="h-4 w-4 text-[#7C3AED]" />
              <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Lifestyle & Fit</span>
              <span className="text-[10px] text-stone-400">Optional — narrows results by career, transport, culture, and setting</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
              {/* Career & Industry */}
              <div data-testid="matcher-career-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                    <Briefcase className="h-4 w-4 text-[#7C3AED]" /> Career & industry
                  </label>
                  <span className="text-xs font-medium text-[#002147]" data-testid="matcher-career-value">
                    {careerMin === 0 ? "Any" : `≥ ${careerMin}/5`}
                  </span>
                </div>
                {handoffSource && careerSectors.length > 0 && (
                  <div
                    className="mt-1 mb-3 flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-[11px] text-violet-900"
                    data-testid="matcher-handoff-banner"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-[#7C3AED] shrink-0 mt-0.5" />
                    <div className="flex-1 leading-snug">
                      <span className="font-semibold">
                        {handoffSource === "coursefit"
                          ? "Suggested from your Course Fit result:"
                          : "Pre-selected from your link:"}
                      </span>{" "}
                      <span>
                        Filtering cities with strong{" "}
                        <span className="font-medium">
                          {careerSectors
                            .map((k) => (CAREER_SECTORS.find((s) => s.key === k)?.label) || k)
                            .join(" · ")}
                        </span>{" "}
                        sectors. Adjust or clear below.
                      </span>
                    </div>
                    <button
                      onClick={clearHandoff}
                      data-testid="matcher-handoff-clear"
                      className="text-violet-500 hover:text-violet-800 p-0.5 shrink-0"
                      aria-label="Clear the suggested sector filter"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <Slider
                  data-testid="matcher-career-slider"
                  value={[careerMin]}
                  min={0} max={5} step={1}
                  onValueChange={(v) => setCareerMin(v[0])}
                  className="mt-1"
                />
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {CAREER_SECTORS.map((s) => {
                    const on = careerSectors.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        data-testid={`matcher-sector-${s.key}`}
                        onClick={() => toggleSector(s.key)}
                        className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                          on
                            ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                            : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <button
                    data-testid="matcher-more-industries-toggle"
                    onClick={() => setShowMoreIndustries((v) => !v)}
                    className="text-[11px] text-[#7C3AED] hover:underline inline-flex items-center gap-1"
                  >
                    {showMoreIndustries ? "− Fewer industries" : "+ More industries"}
                    {industriesFine.length > 0 && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#7C3AED] text-white font-mono">
                        {industriesFine.length}
                      </span>
                    )}
                  </button>
                  {showMoreIndustries && (
                    <div className="mt-2 flex flex-wrap gap-1.5 pl-3 border-l-2 border-[#7C3AED]/20"
                         data-testid="matcher-industries-fine">
                      {INDUSTRIES_FINE.map((s) => {
                        const on = industriesFine.includes(s.key);
                        return (
                          <button
                            key={s.key}
                            data-testid={`matcher-fine-${s.key}`}
                            onClick={() => toggleFine(s.key)}
                            className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                              on
                                ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                                : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  Proximity to employers, corporate hubs, and startup scenes. A city qualifies for a chip when its sector strength is <strong>≥ 7/10</strong> (or ≥ 6/10 for niche industries like Aerospace, Cleantech, Mobility, AgriTech, EdTech).
                </p>
                {/* Entry points to industry-hub landing pages */}
                {(careerSectors.length + industriesFine.length) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="matcher-hub-links">
                    {[...careerSectors, ...industriesFine].map((k) => (
                      <Link
                        key={k}
                        to={`/cities/hubs/${k}`}
                        data-testid={`matcher-hub-link-${k}`}
                        className="text-[11px] text-[#7C3AED] hover:underline inline-flex items-center gap-0.5"
                      >
                        See top {SECTOR_LABELS[k] || k} cities <ArrowRight className="h-3 w-3" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Transport & Accessibility */}
              <div data-testid="matcher-transport-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                    <Train className="h-4 w-4 text-[#7C3AED]" /> Transport & access
                  </label>
                  <span className="text-xs font-medium text-[#002147]" data-testid="matcher-transport-value">
                    {transportModes.length === 0 ? "Any modes" : `${transportModes.length} mode${transportModes.length === 1 ? "" : "s"} ≥ ${transportMin}/5`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {TRANSPORT_MODES.map((m) => {
                    const on = transportModes.includes(m.key);
                    return (
                      <button
                        key={m.key}
                        data-testid={`matcher-mode-${m.key}`}
                        onClick={() => toggleTransport(m.key)}
                        className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                          on
                            ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                            : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                <Slider
                  data-testid="matcher-transport-slider"
                  value={[transportMin]}
                  min={1} max={5} step={1}
                  onValueChange={(v) => setTransportMin(v[0])}
                  disabled={transportModes.length === 0}
                  className={`mt-1 ${transportModes.length === 0 ? "opacity-40" : ""}`}
                />
                <p className="text-xs text-stone-500 mt-2">
                  Pick required modes (rail, air, walk, cycle, night) then the minimum score each must clear.
                </p>
              </div>

              {/* Lifestyle & Culture — Must-have (AND) + Nice-to-have (OR) */}
              <div data-testid="matcher-lifestyle-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                    <Palette className="h-4 w-4 text-[#7C3AED]" /> Lifestyle & culture
                  </label>
                  <span className="text-xs font-medium text-[#002147]" data-testid="matcher-lifestyle-value">
                    {(lifestyleMust.length + lifestyleNice.length) === 0
                      ? "Any"
                      : `${lifestyleMust.length} must · ${lifestyleNice.length} nice`}
                  </span>
                </div>

                {/* Must-have (AND) */}
                <div className="mb-3" data-testid="matcher-lifestyle-must-block">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Must have</span>
                    <span className="text-[10px] text-stone-400">AND — all selected required</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {LIFESTYLE_TAGS.map((t) => {
                      const on = lifestyleMust.includes(t.key);
                      return (
                        <button
                          key={t.key}
                          data-testid={`matcher-must-${t.key}`}
                          onClick={() => toggleMust(t.key)}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                            on
                              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                              : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Nice-to-have (OR) */}
                <div data-testid="matcher-lifestyle-nice-block">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Nice to have</span>
                    <span className="text-[10px] text-stone-400">OR — any one is enough</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {LIFESTYLE_TAGS.map((t) => {
                      const on = lifestyleNice.includes(t.key);
                      const alreadyMust = lifestyleMust.includes(t.key);
                      return (
                        <button
                          key={t.key}
                          data-testid={`matcher-nice-${t.key}`}
                          onClick={() => !alreadyMust && toggleNice(t.key)}
                          disabled={alreadyMust}
                          title={alreadyMust ? "Already in must-have" : ""}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                            alreadyMust
                              ? "bg-stone-50 text-stone-300 border-stone-100 cursor-not-allowed"
                              : on
                              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                              : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <p className="text-xs text-stone-500 mt-2">
                  A city “has” a tag when it scores <strong>≥ 7/10</strong> for that category in our research CSV. Cities must clear every must-have chip and at least one nice-to-have chip (when set).
                </p>
              </div>

              {/* Climate — WUA_Climate_Filter_Developer_Pack v1.0 */}
              <div data-testid="matcher-climate-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                    <CloudSun className="h-4 w-4 text-[#7C3AED]" /> Climate
                  </label>
                  <span className="text-xs font-medium text-[#002147]" data-testid="matcher-climate-value">
                    {climatePrefs.length === 0 && climateAvoids.length === 0
                      ? "Any"
                      : `${climatePrefs.length} pref · ${climateAvoids.length} avoid · min ${climateMin}`}
                  </span>
                </div>

                {/* Q1 — climate preferences (pick up to 2, OR-across) */}
                <div className="mb-3" data-testid="matcher-climate-prefs-block">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">What climate would you enjoy?</span>
                    <span className="text-[10px] text-stone-400">up to 2 · OR</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {CLIMATE_PREF_OPTIONS.map((t) => {
                      const on = climatePrefs.includes(t.key);
                      const disabled = !on && climatePrefs.length >= 2;
                      return (
                        <button
                          key={t.key}
                          data-testid={`matcher-climate-pref-${t.key}`}
                          onClick={() => !disabled && setClimatePrefs((prev) =>
                            prev.includes(t.key) ? prev.filter((k) => k !== t.key) : [...prev, t.key]
                          )}
                          disabled={disabled}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                            disabled
                              ? "bg-stone-50 text-stone-300 border-stone-100 cursor-not-allowed"
                              : on
                              ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                              : "bg-white text-stone-700 border-stone-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Q2 — weather to avoid (any) */}
                <div className="mb-3" data-testid="matcher-climate-avoids-block">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Anything to avoid?</span>
                    <span className="text-[10px] text-stone-400">optional · penalises match</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {CLIMATE_AVOID_OPTIONS.map((t) => {
                      const on = climateAvoids.includes(t.key);
                      return (
                        <button
                          key={t.key}
                          data-testid={`matcher-climate-avoid-${t.key}`}
                          onClick={() => setClimateAvoids((prev) =>
                            prev.includes(t.key) ? prev.filter((k) => k !== t.key) : [...prev, t.key]
                          )}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
                            on
                              ? "bg-red-500 text-white border-red-500"
                              : "bg-white text-stone-700 border-stone-200 hover:border-red-400 hover:text-red-500"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Cutoff slider — hard filter */}
                {(climatePrefs.length > 0 || climateAvoids.length > 0) && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Minimum climate match</span>
                      <span className="text-xs font-mono text-[#7C3AED]">≥ {climateMin}/100</span>
                    </div>
                    <input
                      type="range" min={0} max={100} step={5}
                      value={climateMin}
                      onChange={(e) => setClimateMin(Number(e.target.value))}
                      data-testid="matcher-climate-min-slider"
                      className="w-full accent-[#7C3AED]"
                    />
                  </div>
                )}

                <p className="text-xs text-stone-500 mt-2">
                  Cities scoring below the cutoff are excluded (hard filter). Each avoidance multiplies the match by <strong>1 − 0.60 × risk/100</strong>. Uncurated cities without climate data are excluded when a filter is active.
                </p>
              </div>

              {/* Setting — Campus town ↔ Metropolis */}
              <div data-testid="matcher-setting-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 text-[#7C3AED]" /> Setting
                  </label>
                  <span className="text-xs font-medium text-[#002147]" data-testid="matcher-setting-value">
                    {settingTarget === 0 ? "Any setting" : SETTING_LABELS[settingTarget]}
                  </span>
                </div>
                <Slider
                  data-testid="matcher-setting-slider"
                  value={[settingTarget]}
                  min={0} max={5} step={1}
                  onValueChange={(v) => setSettingTarget(v[0])}
                  className="mt-1"
                />
                <div className="flex justify-between text-[11px] text-stone-500 mt-1.5">
                  <span>Any</span>
                  <span>Campus town</span>
                  <span>Big city</span>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  Slide toward a self-contained campus town or a bustling metropolis (±1 tolerance).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compare bar */}
      {compareSlugs.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 mb-4" data-testid="matcher-compare-bar">
          <div className="bg-[#002147] text-white rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="text-sm flex items-center gap-2">
              <Scale className="h-4 w-4" />
              <span className="font-medium">{compareSlugs.length}/4 cities selected for compare</span>
              <span className="text-white/60 text-xs">— {compareSlugs.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline"
                data-testid="matcher-clear-compare"
                onClick={() => setCompareSlugs([])}
                className="bg-transparent text-white border-white/30 hover:bg-white/10"
              >Clear</Button>
              <Button
                size="sm"
                data-testid="matcher-open-compare"
                disabled={compareSlugs.length < 2}
                onClick={openCompare}
                className="bg-white text-[#002147] hover:bg-white/90"
              >
                Compare side-by-side <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Confirm-cities panel — the explicit gate that turns the PLACE
          chip green. Uses the compare-shortlist as the pool of picks;
          a student who wants to keep exploring can skip and come back. */}
      {compareSlugs.length > 0 && (
        <section id="confirm-place" className="max-w-6xl mx-auto px-6 lg:px-10 mb-6 space-y-3">
          <PlaceReadinessCard testId="matcher-place-readiness" />
          <ConfirmPillarPanel
            pillar="place"
            choices={compareSlugs
              .map((slug) => {
                const c = (results?.cities || []).find((x) => x.slug === slug);
                return c ? { slug: c.slug, name: c.name, country: c.country } : { slug, name: slug };
              })}
            min={1}
            cap={4}
            renderItem={(c) => c.name || c.slug}
            nextTo="/admission"
            testId="matcher-confirm-panel"
            stayOnPage
          />
        </section>
      )}

      {/* Results */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-editorial text-2xl text-[#002147]">
            {loading ? "Matching…" : `${results?.matched_count ?? 0} cities fit`}
          </h2>
          <span className="text-xs text-stone-500" data-testid="matcher-result-summary">
            {results ? `Reviewed ${results.total_candidates_reviewed} · showing top ${results.returned_count}` : ""}
          </span>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-4" data-testid="matcher-error">
            {err}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-stone-200 h-56 animate-pulse" />
            ))}
          </div>
        )}

        {/* Subject-coverage banner — driven by the PICK stage. Lets
            the student toggle each confirmed subject on/off; when any
            are ticked we show only cities offering at least one of
            them (union semantics). */}
        {pickedSubjects.length > 0 && Object.keys(perSubject).length > 0 && results && results.cities.length > 0 && (() => {
          const anyOffered = results.cities.some((c) => offeredByCity(c.slug).length > 0);
          return (
            <div
              className="mb-4 rounded-2xl border border-[#7C3AED]/20 bg-[#7C3AED]/5 px-4 py-3 flex flex-wrap items-center gap-3"
              data-testid="matcher-subject-banner"
            >
              <span className="text-[11px] uppercase tracking-wider text-[#7C3AED] font-medium">
                From your Pick
              </span>
              <span className="text-sm text-stone-700">
                {anyOffered
                  ? <>Tick a subject below to only show cities that offer it.</>
                  : <>None of the cities on this page offer <strong>{pickedSubjects.join(" or ")}</strong>.</>}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {pickedSubjects.map((subj) => {
                  const active = activeSubjectFilter.has(subj);
                  return (
                    <label
                      key={subj}
                      className={[
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium cursor-pointer transition-colors select-none",
                        active
                          ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                          : "bg-white text-[#7C3AED] border-[#7C3AED]/40 hover:border-[#7C3AED]",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActiveSubjectFilter((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(subj);
                          else next.delete(subj);
                          return next;
                        })}
                        data-testid={`matcher-subject-toggle-${subj.replace(/\s+/g, "-").toLowerCase()}`}
                        className="h-3.5 w-3.5 rounded border-white/40 accent-white"
                      />
                      Show only cities with {subj}
                    </label>
                  );
                })}
                {activeSubjectFilter.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveSubjectFilter(new Set())}
                    data-testid="matcher-subject-toggle-clear"
                    className="text-[11px] text-stone-600 hover:text-[#7C3AED] underline underline-offset-2"
                  >
                    Show all
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {!loading && results && results.cities.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-xl p-10 text-center" data-testid="matcher-empty">
            <p className="text-stone-600">
              No cities fit these filters. Try raising the budget, lowering the crime tolerance, or clearing the degree filter.
            </p>
          </div>
        )}

        {!loading && results && results.cities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matcher-results-grid">
            {results.cities.filter((c) => cityMatchesFilter(c.slug)).map((c) => {
              const grade = c.safety?.grade || "?";
              const inCompare = compareSlugs.includes(c.slug);
              const reasons = whyReasons(c, {
                careerMin, careerSectors, industriesFine, transportModes, transportMin,
                lifestyleMust, lifestyleNice, settingTarget,
              });
              return (
                <div key={c.slug}
                     className="bg-white border border-stone-200 rounded-xl p-5 hover:border-[#7C3AED] hover:shadow-md transition group"
                     data-testid={`matcher-card-${c.slug}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-editorial text-xl leading-tight text-[#002147]">{c.name}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" /> {c.country}
                      </div>
                    </div>
                    <Badge className={`${SAFETY_COLOR[grade] || "bg-stone-100 text-stone-700 border-stone-200"} border font-mono text-[11px]`}>
                      Safety {grade}
                    </Badge>
                  </div>

                  {/* Subject-coverage chip — visible only when the user
                      arrived from PICK. Green when this city offers
                      ANY of the confirmed subjects (with per-subject
                      counts); muted amber when none are offered. */}
                  {pickedSubjects.length > 0 && Object.keys(perSubject).length > 0 && (() => {
                    const offered = offeredByCity(c.slug); // array of subject names
                    const missing = pickedSubjects.filter((s) => !offered.includes(s));
                    const hasAny = offered.length > 0;
                    return (
                      <div
                        data-testid={`matcher-subject-chip-${c.slug}`}
                        className={[
                          "mb-2 inline-flex flex-wrap items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium max-w-full",
                          hasAny
                            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : "bg-amber-50 border-amber-200 text-amber-900",
                        ].join(" ")}
                      >
                        <span aria-hidden="true">{hasAny ? "✓" : "—"}</span>
                        {hasAny ? (
                          <>
                            {offered.map((subj, i) => (
                              <span key={subj}>
                                {subj} · {perSubject[subj][c.slug].count} uni
                                {perSubject[subj][c.slug].count === 1 ? "" : "s"}
                                {i < offered.length - 1 ? "," : ""}
                              </span>
                            ))}
                            {missing.length > 0 && (
                              <span className="text-emerald-700/60">
                                (no {missing.join(", ")})
                              </span>
                            )}
                          </>
                        ) : (
                          <span>No {pickedSubjects.join(", ")} here</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Why this fits — top 2 dimensions the user matched on */}
                  {reasons.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid={`matcher-why-${c.slug}`}
                          className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/30 text-[#7C3AED] hover:bg-[#7C3AED]/15 transition"
                        >
                          <HelpCircle className="h-3 w-3" />
                          Why this fits
                          <span className="font-mono text-stone-500">·</span>
                          <span className="truncate max-w-[140px]">{reasons[0].label}</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top" align="start"
                        data-testid={`matcher-why-popover-${c.slug}`}
                        className="w-72 p-4"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1">
                          <Sparkles className="h-3 w-3 text-[#7C3AED]" /> Top match reasons
                        </div>
                        <div className="text-sm font-semibold text-[#002147] mb-2">
                          {c.name} — {c.country}
                        </div>
                        <ul className="space-y-2">
                          {reasons.map((r, i) => {
                            const Icon = REASON_ICON[r.icon] || Sparkles;
                            return (
                              <li key={i} className="flex items-start gap-2 text-xs text-stone-700">
                                <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#7C3AED]/10 text-[#7C3AED] shrink-0">
                                  <Icon className="h-3 w-3" />
                                </span>
                                <span>{r.label}</span>
                              </li>
                            );
                          })}
                        </ul>
                        <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
                          Reasons prioritise filters you turned on. When none are active, we surface the city's biggest organic strengths.
                        </p>
                      </PopoverContent>
                    </Popover>
                  )}

                  {/* Money row */}
                  <div className="flex items-baseline justify-between border-t border-stone-100 pt-3 mt-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Monthly cost</div>
                      <div className="font-mono text-lg text-[#002147]">
                        {format(c.cost_of_living.total_monthly_usd, { decimals: 0 })}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700">Budget slack</div>
                      <div className="font-mono text-sm text-emerald-700 font-semibold">
                        +{format(c.budget_slack_usd, { decimals: 0 })}
                      </div>
                    </div>
                  </div>

                  {/* Best for chips */}
                  {c.best_for?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {c.best_for.slice(0, 2).map((b, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-50 border border-stone-200 text-stone-600">
                          {b.length > 40 ? b.slice(0, 40) + "…" : b}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Law-firm strip — visible only when Law filter is active */}
                  {careerSectors.includes("law") && c.dimensions?.career?.law_firms?.length > 0 && (
                    <div className="mt-3 border-t border-stone-100 pt-3" data-testid={`matcher-law-firms-${c.slug}`}>
                      <div className="text-[9px] uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
                        <Briefcase className="h-2.5 w-2.5" />
                        Law firms nearby · {c.dimensions.career.law_firms.length}/13 sampled
                      </div>
                      <div className="text-[11px] text-stone-700 leading-snug">
                        {c.dimensions.career.law_firms.slice(0, 4).join(" · ")}
                        {c.dimensions.career.law_firms.length > 4 && (
                          <span className="text-stone-400"> · +{c.dimensions.career.law_firms.length - 4} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Meta row */}
                  <div className="mt-3 flex items-center justify-between text-[11px] text-stone-500">
                    <span className="flex items-center gap-1">
                      <GraduationCap className="h-3 w-3" />
                      {c.universities_in_city_count} uni{c.universities_in_city_count === 1 ? "" : "s"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Student score {c.student_friendliness_score ?? "—"}/5
                    </span>
                  </div>

                  {/* Dimensions row — Career / Transport / Setting */}
                  {c.dimensions && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`matcher-dimensions-${c.slug}`}>
                      {c.dimensions.career?.score ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-800 flex items-center gap-1">
                          <Briefcase className="h-2.5 w-2.5" /> Career {c.dimensions.career.score}/5
                        </span>
                      ) : null}
                      {c.dimensions.transport && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 flex items-center gap-1">
                          <Train className="h-2.5 w-2.5" />
                          Rail {c.dimensions.transport.rail}/5 · Walk {c.dimensions.transport.walk}/5
                        </span>
                      )}
                      {c.dimensions.setting ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-stone-700 flex items-center gap-1">
                          <Building2 className="h-2.5 w-2.5" /> {SETTING_LABELS[c.dimensions.setting]}
                        </span>
                      ) : null}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      asChild size="sm"
                      className="flex-1 bg-[#002147] text-white hover:bg-[#002147]/90"
                      data-testid={`matcher-view-${c.slug}`}
                    >
                      <Link to={`/cities/${c.slug}`}>See city guide</Link>
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => toggleCompare(c.slug)}
                      disabled={!inCompare && compareSlugs.length >= 4}
                      data-testid={`matcher-add-compare-${c.slug}`}
                      className={inCompare ? "bg-[#7C3AED] text-white border-[#7C3AED]" : ""}
                    >
                      <Scale className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Method footer */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-white/70 border border-stone-200 rounded-lg p-5 text-xs text-stone-600 leading-relaxed" data-testid="matcher-methodology">
          <div className="font-semibold text-stone-700 mb-1 flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5" /> How this works
          </div>
          <p>
            We take each city's Numbeo-sourced monthly student cost (Jan 2026), convert to your currency, and keep only cities within your budget.
            Crime tolerance uses each city's Numbeo Crime Index (grade A+ safest to D). "Lifestyle & Fit" scores Career, Transport (rail/air/walk/cycle/night), Culture, and Setting — hand-curated for the top ~35 student cities and inferred from population, country and student-friendliness for the rest. When any Lifestyle & Fit filter is active, ranking prioritises the fit score; otherwise it's safety-first.
            Degree filter checks every course title, tag, and subject area across ~30,500 degrees.
          </p>
        </div>
      </section>
    </div>
  );
}
